// Onboarding ViewModel - Main orchestration for onboarding flow
// Determines if onboarding should be shown, manages step state, coordinates VMs

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { browserHistory } from 'react-router';
import {
  Async,
  toAsyncFetched,
  Actions,
  Dispatchers,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { PATHS } from '@client/routes';
import { navigate } from '@client/new/helpers/history';
import auth from '@client/components/auth';
import { trackEvent } from '@client/new/services/tracking';
import { useOnboardingModel } from '../model/Onboarding.model';
import {
  OnboardingQuestion,
  OnboardingResponse,
  OnboardingDismissal,
  ScoringCategory,
} from '../model/Onboarding.types';
import { isEligibleForOnboarding } from '../model/onboardingEligibility';
import { QuestionVM, createQuestionVM } from './Question.viewModel';
import { RadarChartVM, createRadarChartVM } from './RadarChart.viewModel';
import {
  StrategyVM,
  CaseStudyVM,
  createStrategyVM,
  sortStrategiesWithRecommendedFirst,
} from './Strategy.viewModel';
import {
  OnboardingStep,
  parseStep,
  buildStepUrl,
  getNextStep,
  getPreviousStep,
  isModalityStep as checkIsModalityStep,
  isRevenueGoalStep as checkIsRevenueGoalStep,
  isStrategyStep as checkIsStrategyStep,
  isResultStep as checkIsResultStep,
  getQuestionIndex,
  getStepDisplayNumber,
  getTotalDisplaySteps,
} from './OnboardingStep.viewModel';
import {
  REVENUE_GOAL_QUESTION_ID,
  DEFAULT_REVENUE_POSITION,
  clampPosition,
} from './RevenueGoal.viewModel';
import {
  MODALITY_QUESTION_ID,
  parseModalitySelection,
} from './Modality.viewModel';

// Re-export step type for convenience
export type { OnboardingStep } from './OnboardingStep.viewModel';

// Action map for dispatcher
export type OnboardingVMActionMap = {
  GoNext: null;
  GoBack: null;
  GoToQuestion: number;
  GoToStrategy: null;
  SelectAnswer: { questionId: string; answerId: string };
  SelectStrategy: string;
  Dismiss: null;
  ClearStrategy: null;
  CloseReview: null;
  SubmitTextAnswer: { questionId: string; text: string };
  ResetOnboarding: null;
  SkipToStrategy: null;
  SubmitModality: readonly string[];
  SubmitRevenueGoal: number;
};

// Answer summary chip for display below radar chart
export type AnswerSummaryVM = {
  id: string;
  questionId: string;
  categoryId: string;
  categoryName: string;
  answerText: string;
};

// Main ViewModel type
export type OnboardingVM = {
  shouldShow: boolean;
  currentStep: OnboardingStep;
  currentQuestionIndex: number | null;
  isModalityStep: boolean;
  isRevenueGoalStep: boolean;
  // Bracket position (1-5) the revenue-goal slider should start on; seeds from
  // an existing response, otherwise the middle default.
  revenueGoalInitialPosition: number;
  isStrategyStep: boolean;
  isResultStep: boolean;
  isReviewMode: boolean;
  totalSteps: number;
  // Display denominator for the progress bar. Always includes the modality and
  // revenue-goal steps.
  totalDisplaySteps: number;
  // 1-indexed display number for the current numbered step. `null` on result
  // and strategy steps which don't use the numbered progress indicator.
  currentDisplayStep: number | null;
  currentQuestion: QuestionVM | null;
  radarData: RadarChartVM;
  answeredSummary: AnswerSummaryVM[];
  strategies: StrategyVM[];
  allCaseStudies: CaseStudyVM[];
  resultAvatarUrls: [string, string];
  localResponses: OnboardingResponse[];
  isSaving: boolean;
  // Selection parsed from the stored modality response; seeds the modality
  // step's initial selection on (re)entry.
  modalityInitialSelection: readonly string[];
} & Dispatchers<OnboardingVMActionMap>;

// Check if onboarding should be shown based on 4 conditions
export const shouldShowOnboarding = (
  creatorCreatedAt: Date | string | null,
  responses: OnboardingResponse[],
  dismissal: OnboardingDismissal,
  selectedStrategyId: string | null,
): boolean => {
  // 1. Don't show for creators who created their account before launch
  if (!isEligibleForOnboarding(creatorCreatedAt)) {
    return false;
  }

  // 2. Don't show if user has ANY responses saved
  if (responses.length > 0) {
    return false;
  }

  // 3. Don't show if dismissed (webDismissedAt is set)
  if (dismissal.webDismissedAt) {
    return false;
  }

  // 4. Don't show if user already has selected strategy
  if (selectedStrategyId) {
    return false;
  }

  return true;
};

// Find response for a question (local or saved)
const findResponse = (
  questionId: string,
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
): OnboardingResponse | null => {
  // Local responses take precedence
  const local = localResponses.find((r) => r.questionId === questionId);
  if (local) return local;

  const saved = savedResponses.find((r) => r.questionId === questionId);
  return saved ?? null;
};

const mergeResponses = (
  savedResponses: OnboardingResponse[],
  localResponses: OnboardingResponse[],
): OnboardingResponse[] => {
  const byQuestion = new Map<string, OnboardingResponse>();

  savedResponses.forEach((response) => {
    byQuestion.set(response.questionId, response);
  });

  localResponses.forEach((response) => {
    byQuestion.set(response.questionId, response);
  });

  return Array.from(byQuestion.values());
};

const hasAnsweredAllQuestions = (
  questions: OnboardingQuestion[],
  responses: OnboardingResponse[],
): boolean => {
  if (questions.length === 0) {
    return false;
  }

  return questions.every((question) => {
    const response = responses.find(
      (entry) => entry.questionId === question.id,
    );
    return Boolean(response?.answerId || response?.textValue);
  });
};

const useDebouncedCallback = <T extends (...args: unknown[]) => void>(
  callback: T,
  delay: number,
): T => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCallbackRef = useRef(callback);

  useEffect(() => {
    latestCallbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        latestCallbackRef.current(...args);
      }, delay);
    },
    [delay],
  );

  return debounced as T;
};

const shouldRedirect = (
  currentStep: OnboardingStep,
  validStep: OnboardingStep,
): boolean => {
  if (currentStep.type !== validStep.type) {
    return true;
  }

  if (
    currentStep.type === 'question' &&
    validStep.type === 'question' &&
    currentStep.index !== validStep.index
  ) {
    return true;
  }

  // Modality, result, and strategy steps have no positional index — equal type
  // means same step.
  return false;
};

/**
 * Builds answer summary chips filtered up to the current step (inclusive).
 *
 * Used for the chart-adjacent chips so "time travel" reflects the current
 * question state when navigating back in the flow.
 *
 * @param questions - Onboarding questions in order
 * @param localResponses - Unsaved responses for the session
 * @param savedResponses - Persisted responses from the API
 * @param scoringCategories - Category metadata for display names
 * @param currentQuestionIndex - Current step index (null = include all)
 * @returns Answer summary chips for categorized questions only
 */
export const createAnswerSummary = (
  questions: OnboardingQuestion[],
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
  scoringCategories: ScoringCategory[],
  currentQuestionIndex: number | null,
): AnswerSummaryVM[] => {
  const summaries: AnswerSummaryVM[] = [];
  const categoryNameById = new Map(
    scoringCategories.map((category) => [category.id, category.name]),
  );

  // If on strategy step (currentQuestionIndex is null), show all answered questions
  // Otherwise, include questions up to the current index (inclusive)
  const maxIndex =
    currentQuestionIndex !== null
      ? Math.min(currentQuestionIndex + 1, questions.length)
      : questions.length;

  for (let i = 0; i < maxIndex; i++) {
    const question = questions[i];
    if (!question.targetCategory) continue;

    const response = findResponse(question.id, localResponses, savedResponses);
    if (!response?.answerId) continue;

    const answer = question.answers.find((a) => a.id === response.answerId);
    if (!answer) continue;
    const categoryName =
      categoryNameById.get(question.targetCategory) ?? question.targetCategory;

    summaries.push({
      id: answer.id,
      questionId: question.id,
      categoryId: question.targetCategory,
      categoryName,
      answerText: answer.title,
    });
  }

  return summaries;
};

// Get all case studies from strategies
const getAllCaseStudies = (strategies: StrategyVM[]): CaseStudyVM[] => {
  const seen = new Set<string>();
  const caseStudies: CaseStudyVM[] = [];

  for (const strategy of strategies) {
    for (const cs of strategy.caseStudies) {
      if (!seen.has(cs.id)) {
        seen.add(cs.id);
        caseStudies.push(cs);
      }
    }
  }

  return caseStudies;
};

// Navigate to step using browser history
const navigateToStep = (step: OnboardingStep): void => {
  const url = buildStepUrl(step);
  browserHistory.push(url);
};

const buildGuideUrl = (
  creatorId: number,
  strategyId: string | null,
): string => {
  if (!strategyId) {
    return PATHS.GUIDE(creatorId);
  }
  const params = new URLSearchParams({ strategyId });
  return `${PATHS.GUIDE(creatorId)}?${params.toString()}`;
};

// Find the first unanswered question index
// Returns the index of first question without a response, or questions.length if all answered
export const findFirstUnansweredIndex = (
  questions: OnboardingQuestion[],
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
): number => {
  for (let i = 0; i < questions.length; i++) {
    const response = findResponse(
      questions[i].id,
      localResponses,
      savedResponses,
    );
    if (!response?.answerId && !response?.textValue) {
      return i;
    }
  }
  return questions.length; // All answered
};

// Get the valid step based on answered questions. Modality is the first step
// of the flow and is always a valid step (the entry redirect leads creators
// there first; explicit `?step=` URLs are honored, like the revenue-goal
// slider). For later steps, normal question gating applies — a step is valid
// only if all previous questions are answered.
export const getValidStep = (
  requestedStep: OnboardingStep,
  questions: OnboardingQuestion[],
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
): OnboardingStep => {
  // Modality is always a valid step and is always available.
  if (requestedStep.type === 'modality') {
    return requestedStep;
  }

  const firstUnanswered = findFirstUnansweredIndex(
    questions,
    localResponses,
    savedResponses,
  );

  // If all questions answered, any step is valid (including result and strategy)
  if (firstUnanswered >= questions.length) {
    return requestedStep;
  }

  // If requested step is result but not all answered, go to first unanswered
  // Strategy step is allowed at any time (via "Skip for now" button)
  if (requestedStep.type === 'result') {
    return { type: 'question', index: firstUnanswered };
  }

  // Allow strategy step even if questions aren't answered
  if (requestedStep.type === 'strategy') {
    return requestedStep;
  }

  // If requested question index is beyond first unanswered, redirect
  if (
    requestedStep.type === 'question' &&
    requestedStep.index > firstUnanswered
  ) {
    return { type: 'question', index: firstUnanswered };
  }

  return requestedStep;
};

export const useOnboardingViewModel = (
  creatorCreatedAt: Date | string | null = null,
  selectedStrategyId: string | null = null,
): Async<OnboardingVM> => {
  const influencerId = useInfluencerId();
  const model = useOnboardingModel();
  const [localResponses, setLocalResponses] = useState<OnboardingResponse[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const latestResponsesRef = useRef<OnboardingResponse[]>([]);

  useEffect(() => {
    latestResponsesRef.current = localResponses;
  }, [localResponses]);

  // Get total questions count (0 when not fetched)
  const totalQuestions =
    model.type === 'fetched' ? model.value.questions.length : 0;

  // Parse current step from URL. Memoized against `window.location.search`
  // so that a no-op re-render doesn't produce a new `currentStep` object
  // reference (which would cascade through every dependent useCallback /
  // useEffect and produce thrash).
  const locationSearch = window.location.search;
  const currentStep = useMemo(
    () => parseStep(totalQuestions),
    [locationSearch, totalQuestions],
  );

  // Extract model data (empty defaults when not fetched)
  const noopAsync = useCallback(async () => {
    // noop for non-fetched state
  }, []);

  const modelData =
    model.type !== 'fetched'
      ? {
          questions: [] as OnboardingQuestion[],
          savedResponses: [] as OnboardingResponse[],
          dismissal: { webDismissedAt: null, mobileDismissedAt: null },
          strategies: [],
          scoringCategories: [],
          saveResponses: noopAsync,
          saveStrategy: noopAsync,
          clearStrategy: noopAsync,
          dismiss: noopAsync,
          resetOnboarding: noopAsync,
        }
      : {
          questions: model.value.questions,
          savedResponses: model.value.responses,
          dismissal: model.value.dismissal,
          strategies: model.value.strategies,
          scoringCategories: model.value.scoringCategories,
          saveResponses: model.value.saveResponses,
          saveStrategy: model.value.saveStrategy,
          clearStrategy: model.value.clearStrategy,
          dismiss: model.value.dismiss,
          resetOnboarding: model.value.resetOnboarding,
        };

  const {
    questions,
    savedResponses,
    dismissal,
    strategies,
    scoringCategories,
    saveResponses,
    saveStrategy,
    clearStrategy,
    dismiss,
    resetOnboarding,
  } = modelData;

  // The modality selection is held like any other answer: in `localResponses`
  // until the batch save fires once all questions are answered. It is
  // "answered" once a value exists locally or saved, and that drives both the
  // entry gate and seeding the picker's initial selection.
  const modalityResponse = findResponse(
    MODALITY_QUESTION_ID,
    localResponses,
    savedResponses,
  );
  const modalityAnswered = Boolean(modalityResponse?.textValue);
  const modalityInitialSelection = parseModalitySelection(
    modalityResponse?.textValue,
  );

  // The revenue-goal slider answer is held like any other answer: in
  // `localResponses` until the batch save fires once all questions are
  // answered. It is "answered" once a value exists locally or saved.
  const revenueGoalResponse = findResponse(
    REVENUE_GOAL_QUESTION_ID,
    localResponses,
    savedResponses,
  );
  const revenueGoalAnswered = Boolean(revenueGoalResponse?.textValue);
  const revenueGoalInitialPosition = revenueGoalResponse?.textValue
    ? clampPosition(parseInt(revenueGoalResponse.textValue, 10))
    : DEFAULT_REVENUE_POSITION;

  const debouncedSave = useDebouncedCallback(() => {
    const responsesToSave = latestResponsesRef.current;
    const mergedResponses = mergeResponses(savedResponses, responsesToSave);
    if (!hasAnsweredAllQuestions(questions, mergedResponses)) return;

    saveResponses(mergedResponses).catch(() => {
      // Ignore save errors - user can continue with local state
    });
  }, 1000);

  // Navigate to a step, emitting `Modality Step Viewed` when the destination
  // is the modality step. Keeps the side effect at the navigation call site
  // instead of in a `useEffect` keyed on `currentStep.type`.
  const navigateToStepWithTracking = useCallback((step: OnboardingStep) => {
    if (step.type === 'modality') {
      trackEvent({ type: 'Modality Step Viewed' });
    }
    navigateToStep(step);
  }, []);

  // Validate step - redirect if user tries to skip ahead. Modality is the
  // entrypoint for every creator: when the URL has no step param (parses to
  // `question` index 0) it leads the flow until answered.
  useEffect(() => {
    if (model.type !== 'fetched' || questions.length === 0) return;

    const hasStepParam =
      new URLSearchParams(window.location.search).get('step') !== null;
    // No-param `/onboarding` lands on the first step of the flow:
    // modality (until answered) → revenue-goal (until answered) → questions.
    // Explicit `?step=` URLs are honored and subject to `getValidStep` gating.
    if (
      !hasStepParam &&
      currentStep.type === 'question' &&
      currentStep.index === 0
    ) {
      if (!modalityAnswered) {
        navigateToStepWithTracking({ type: 'modality' });
        return;
      }
      if (!revenueGoalAnswered) {
        navigateToStep({ type: 'revenue-goal' });
        return;
      }
    }

    const validStep = getValidStep(
      currentStep,
      questions,
      localResponses,
      savedResponses,
    );

    if (shouldRedirect(currentStep, validStep)) {
      navigateToStepWithTracking(validStep);
    }
  }, [
    model.type,
    currentStep,
    questions,
    localResponses,
    savedResponses,
    modalityAnswered,
    revenueGoalAnswered,
    navigateToStepWithTracking,
  ]);

  const saveLocalResponses = useCallback(async () => {
    if (localResponses.length === 0) return;

    const mergedResponses = mergeResponses(savedResponses, localResponses);
    if (!hasAnsweredAllQuestions(questions, mergedResponses)) return;

    setIsSaving(true);
    try {
      await saveResponses(mergedResponses);
      setLocalResponses([]);
    } finally {
      setIsSaving(false);
    }
  }, [localResponses, saveResponses, savedResponses, questions]);

  // Dispatch handler - must be before early return
  const _dispatch = useCallback(
    async (action: Actions<OnboardingVMActionMap>) => {
      switch (action.type) {
        case 'GoNext': {
          await saveLocalResponses().catch(() => {
            // Best-effort save — responses will be persisted on strategy selection
          });
          const nextStep = getNextStep(currentStep, totalQuestions);
          if (nextStep) {
            navigateToStep(nextStep);
          }
          break;
        }

        case 'GoBack': {
          const prevStep = getPreviousStep(currentStep, totalQuestions);
          if (!prevStep) {
            break;
          }

          if (currentStep.type === 'question') {
            const question = questions[currentStep.index];
            trackEvent({
              type: 'Question Back',
              payload: { 'Question text': question?.title ?? '' },
            });
          }

          navigateToStepWithTracking(prevStep);
          break;
        }

        case 'GoToQuestion': {
          const index = action.payload;
          if (index >= 0 && index < totalQuestions) {
            navigateToStep({ type: 'question', index });
          }
          break;
        }

        case 'GoToStrategy': {
          navigateToStep({ type: 'strategy' });
          break;
        }

        case 'SelectAnswer': {
          const { questionId, answerId } = action.payload;
          const question = questions.find((q) => q.id === questionId);
          const answer = question?.answers.find((a) => a.id === answerId);

          trackEvent({
            type: 'Question Answered',
            payload: {
              'Question text': question?.title ?? '',
              'Question answer': answer?.title ?? '',
            },
          });

          const newResponse: OnboardingResponse = {
            questionId,
            answerId,
            textValue: null,
          };

          // Update local state immediately (optimistic UI)
          setLocalResponses((prev) => {
            const filtered = prev.filter((r) => r.questionId !== questionId);
            return [...filtered, newResponse];
          });

          // Navigate to next step immediately
          const nextStep = getNextStep(currentStep, totalQuestions);
          if (nextStep) {
            navigateToStep(nextStep);
          }

          // Save in background (debounced, fire-and-forget)
          // Local responses persist for the session - don't clear on save
          debouncedSave();
          break;
        }

        case 'SubmitTextAnswer': {
          const { questionId, text } = action.payload;
          setLocalResponses((prev) => {
            const filtered = prev.filter((r) => r.questionId !== questionId);
            return [
              ...filtered,
              { questionId, answerId: null, textValue: text },
            ];
          });
          break;
        }

        case 'SelectStrategy': {
          const strategy = strategies.find((s) => s.id === action.payload);
          trackEvent({
            type: 'Strategy selected',
            payload: { 'Strategy name': strategy?.name ?? '' },
          });

          setIsSaving(true);
          try {
            await saveLocalResponses();
            await saveStrategy(action.payload);
            trackEvent({ type: 'Strategy Guide Launched' });
            navigate(buildGuideUrl(influencerId, action.payload));
          } catch {
            setIsSaving(false);
          }
          break;
        }

        case 'Dismiss': {
          trackEvent({ type: 'Strategy dismissed' });

          setIsSaving(true);
          try {
            await saveLocalResponses();
            await clearStrategy();
            await dismiss();
            const homePath = auth.isInfluencer()
              ? PATHS.HOME
              : PATHS.HOME_CREATOR(influencerId);
            navigate(homePath);
          } finally {
            setIsSaving(false);
          }
          break;
        }

        case 'ClearStrategy': {
          trackEvent({ type: 'Strategy cleared' });

          setIsSaving(true);
          try {
            await clearStrategy();
            const homePath = auth.isInfluencer()
              ? PATHS.HOME
              : PATHS.HOME_CREATOR(influencerId);
            navigate(homePath);
          } finally {
            setIsSaving(false);
          }
          break;
        }

        case 'CloseReview': {
          const homePath = auth.isInfluencer()
            ? PATHS.HOME
            : PATHS.HOME_CREATOR(influencerId);
          navigate(homePath);
          break;
        }

        case 'ResetOnboarding': {
          setIsSaving(true);
          try {
            await resetOnboarding();
            setLocalResponses([]);
            // Navigate to first question
            navigateToStep({ type: 'question', index: 0 });
          } finally {
            setIsSaving(false);
          }
          break;
        }

        case 'SubmitModality': {
          const selection = action.payload;
          // Stored as a generic onboarding response (JSON array, selection
          // order preserved); persists with the batch save once all questions
          // are answered (same path as the revenue-goal slider).
          setLocalResponses((prev) => {
            const filtered = prev.filter(
              (r) => r.questionId !== MODALITY_QUESTION_ID,
            );
            return [
              ...filtered,
              {
                questionId: MODALITY_QUESTION_ID,
                answerId: null,
                textValue: JSON.stringify(selection),
              },
            ];
          });

          trackEvent({
            type: 'Modality Keywords Submitted',
            payload: { keywordCount: selection.length },
          });

          const nextStep = getNextStep({ type: 'modality' }, totalQuestions);
          if (nextStep) {
            navigateToStep(nextStep);
          }
          break;
        }

        case 'SubmitRevenueGoal': {
          const position = action.payload;
          // Stored as a generic onboarding response; persists with the batch
          // save once all questions are answered (same path as text answers).
          setLocalResponses((prev) => {
            const filtered = prev.filter(
              (r) => r.questionId !== REVENUE_GOAL_QUESTION_ID,
            );
            return [
              ...filtered,
              {
                questionId: REVENUE_GOAL_QUESTION_ID,
                answerId: null,
                textValue: String(position),
              },
            ];
          });

          const nextStep = getNextStep(
            { type: 'revenue-goal' },
            totalQuestions,
          );
          if (nextStep) {
            navigateToStep(nextStep);
          }
          break;
        }

        case 'SkipToStrategy': {
          const currentQuestion =
            currentStep.type === 'question'
              ? questions[currentStep.index]
              : null;
          const response = currentQuestion
            ? findResponse(currentQuestion.id, localResponses, savedResponses)
            : null;
          const answer = currentQuestion?.answers.find(
            (a) => a.id === response?.answerId,
          );

          trackEvent({
            type: 'Question Skipped',
            payload: {
              'Question text': currentQuestion?.title ?? '',
              'Question answer': answer?.title ?? '',
            },
          });

          setIsSaving(true);
          try {
            await saveLocalResponses();
            await dismiss();
            navigateToStep({ type: 'strategy' });
          } finally {
            setIsSaving(false);
          }
          break;
        }
      }
    },
    [
      saveLocalResponses,
      saveStrategy,
      clearStrategy,
      dismiss,
      resetOnboarding,
      currentStep,
      totalQuestions,
      strategies,
      influencerId,
      debouncedSave,
      navigateToStepWithTracking,
    ],
  );

  // Memoize so child views (Modality.view) receive a stable `dispatch`
  // reference. Without this, every parent render gives the child a new
  // dispatch, which cascades through `useCallback` deps into the modality
  // view-model and rebuilds its internal closures repeatedly.
  const dispatchers = useMemo(
    () =>
      getDispatchers<OnboardingVMActionMap>((action) => {
        _dispatch(action);
      }),
    [_dispatch],
  );

  // Early return if model not ready - after all hooks
  if (model.type !== 'fetched') {
    return model;
  }

  // Determine if onboarding should show
  const shouldShow = shouldShowOnboarding(
    creatorCreatedAt,
    savedResponses,
    dismissal,
    selectedStrategyId,
  );

  // Build current question VM
  const currentQuestionIndex = getQuestionIndex(currentStep);
  const currentQuestionData =
    currentQuestionIndex !== null ? questions[currentQuestionIndex] : null;

  const currentQuestion: QuestionVM | null = currentQuestionData
    ? createQuestionVM(
        currentQuestionData,
        findResponse(currentQuestionData.id, localResponses, savedResponses),
      )
    : null;

  // Build radar chart data (only shows values from questions before current step)
  const radarData = createRadarChartVM(
    questions,
    localResponses,
    savedResponses,
    scoringCategories,
    currentQuestionIndex,
    currentQuestionData?.targetCategory ?? null,
    300, // size
  );

  // Build answer summary (only shows answers from questions before current step)
  const answeredSummary = createAnswerSummary(
    questions,
    localResponses,
    savedResponses,
    scoringCategories,
    currentQuestionIndex,
  );

  // Build strategy VMs, sorted with recommended first
  const strategyVMs = sortStrategiesWithRecommendedFirst(
    strategies.map((s) => createStrategyVM(s)),
  );
  const allCaseStudies = getAllCaseStudies(strategyVMs);

  // Review mode: user already has a selected strategy
  const isReviewMode = strategies.some((s) => s.isSelected);

  // Get first two case study cover images for result screen avatars
  const resultAvatarUrls: [string, string] = [
    allCaseStudies[0]?.coverImageUrl ?? '',
    allCaseStudies[1]?.coverImageUrl ?? '',
  ];

  return toAsyncFetched<OnboardingVM>({
    shouldShow,
    currentStep,
    currentQuestionIndex,
    isModalityStep: checkIsModalityStep(currentStep),
    isRevenueGoalStep: checkIsRevenueGoalStep(currentStep),
    revenueGoalInitialPosition,
    isStrategyStep: checkIsStrategyStep(currentStep),
    isResultStep: checkIsResultStep(currentStep),
    isReviewMode,
    totalSteps: questions.length,
    totalDisplaySteps: getTotalDisplaySteps(questions.length),
    currentDisplayStep: getStepDisplayNumber(currentStep),
    currentQuestion,
    radarData,
    answeredSummary,
    strategies: strategyVMs,
    allCaseStudies,
    resultAvatarUrls,
    localResponses,
    isSaving,
    modalityInitialSelection,
    ...dispatchers,
  });
};
